<template>
  <div class="user-avatar-filter">
    <DropdownTooltip
      placement="bottom-start"
      :shown="opened"
      :triggers="[]"
      :skidding="64"
      popperClass="user-avatar-filter__dropdown"
      iconSize="md"
      @onHideDropdown="hideDropdown"
    >
      <template #tooltip-label>
        <div class="user-avatar-filter__placeholder">
          <div class="user-avatar-filter__avatar-group">
            <div
              v-for="(item, index) in listOfVisibleUsers"
              :key="item.id"
              class="user-avatar-filter__avatar-group__item"
              :class="{ 'is-selected': selectedUser?.id === item.id }"
              :style="{ zIndex: selectedUser?.id === item.id ? sortedUsers.length : index }"
              @click="onUserSelect(item)"
            >
              <Avatar
                v-tooltip.bottom="{ content: item.username, theme: 'info-tooltip-sm' }"
                :src="item.picture?.pictureUrl"
                :gap="item.getInitials()"
                :class="{ 'is-selected': selectedUser?.id === item.id }"
                class="is-scale-by-hover"
                bordered
              />
              <button
                v-if="selectedUser?.id === item.id"
                type="button"
                class="user-avatar-filter__avatar-group__clear"
                @click.stop="clearSelectedUser"
              >
                <icon
                  data="@icon/close2.svg"
                  width="var(--icon-12)"
                  height="var(--icon-12)"
                  class="common-gray-interactive"
                />
              </button>
            </div>
            <div
              v-if="hiddenUsersCount"
              class="avatar is-md is-bordered user-avatar-filter__avatar-group__item avatar__length"
              :style="{ zIndex: numberOfVisibleUsers + 1 }"
              @click="toggle"
            >
              +{{ hiddenUsersCount }}
            </div>
          </div>
        </div>
      </template>
      <template #tooltip-content>
        <div>
          <Observer
            :items="listOfHideUsers"
            :last="last"
            class="custom-scroll user-avatar-filter__list"
            elementClass="user-avatar-filter__item custom-outline"
            @intersect="emit('loadMore')"
          >
            <template #default="{ item }">
              <button
                type="button"
                class="user-avatar-filter__button"
                :tabindex="-1"
                @click.prevent="onUserSelect(item)"
              >
                <UserComponent :user="item" />
              </button>
            </template>
          </Observer>
        </div>
      </template>
    </DropdownTooltip>
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue"

import { Avatar } from "@/components/Avatar"
import DropdownTooltip from "@/components/Dropdown/DropdownTooltip.vue"
import Observer from "@/components/Observer/index.vue"
import { default as UserComponent } from "@/components/User/index.vue"

import { User } from "@/models"

const props = withDefaults(
  defineProps<{
    id: string
    users: User[]
    selectedUser?: User | null
    currentUser?: User | null
    scrollableContainer?: string
    last?: boolean
    totalElements?: number | null
  }>(),
  {
    selectedUser: null,
    currentUser: null,
    scrollableContainer: "",
    last: true,
    totalElements: null
  }
)

const emit = defineEmits<{
  (e: "selectUser", value: User | null): void
  (e: "loadMore"): void
}>()

const opened = ref(false)
const numberOfVisibleUsers = 4

const sortedUsers = computed<User[]>(() => {
  const enabledUsers = [...(props.users ?? [])]
    .filter(user => user.enabled)
    .sort((a, b) => (a.firstName > b.firstName ? 1 : -1))

  const currentUserId = props.currentUser?.id
  const selectedUserId = props.selectedUser?.id
  const isSelectedUserVisible = selectedUserId
    ? enabledUsers.slice(0, numberOfVisibleUsers).some(user => user.id === selectedUserId)
    : false

  return enabledUsers.sort((a, b) => {
    if (currentUserId && a.id === currentUserId) {
      return -1
    }

    if (currentUserId && b.id === currentUserId) {
      return 1
    }

    if (!selectedUserId || isSelectedUserVisible) {
      return 0
    }

    if (a.id === selectedUserId) {
      return -1
    }

    if (b.id === selectedUserId) {
      return 1
    }

    return 0
  })
})

const listOfVisibleUsers = computed(() => sortedUsers.value.slice(0, numberOfVisibleUsers))
const listOfHideUsers = computed(() => sortedUsers.value.slice(numberOfVisibleUsers))
const hiddenUsersCount = computed(() =>
  Math.max((props.totalElements ?? sortedUsers.value.length) - numberOfVisibleUsers, 0)
)

function toggle() {
  opened.value = true
}

function hideDropdown() {
  if (opened.value) {
    opened.value = false
  }
}

function onUserSelect(user: User | null) {
  hideDropdown()
  if (props.selectedUser?.id === user?.id) {
    return clearSelectedUser()
  }
  emit("selectUser", user)
}

function clearSelectedUser() {
  hideDropdown()
  emit("selectUser", null)
}

function getParentScrollableContainer() {
  return props.scrollableContainer ? document.getElementById(props.scrollableContainer) : null
}

onMounted(() => {
  window.addEventListener("scroll", hideDropdown)
  window.addEventListener("resize", hideDropdown)
  getParentScrollableContainer()?.addEventListener("scroll", hideDropdown)
})

onBeforeUnmount(() => {
  window.removeEventListener("scroll", hideDropdown)
  window.removeEventListener("resize", hideDropdown)
  getParentScrollableContainer()?.removeEventListener("scroll", hideDropdown)
})
</script>
